import { create } from 'zustand';
import { EXPORT_POLL_MS, latestExportsRequest } from '@/components/exports/exports';
import ExportModel from '@/models/entities/ExportModel';
import { listPage } from '@/models/listPage';

/** Exports affichés dans le panneau Téléchargements */
export const LATEST_EXPORTS = 5;

interface ExportStore {
  /** Société dont les exports sont suivis (null : aucune, ou pas Dirigeant) */
  companyId: number | null;
  /** Ses derniers exports, du plus récent au plus ancien */
  exports: ExportModel[];
  loaded: boolean;
  /** Exports terminés depuis le dernier coup d'œil au panneau (pastille de l'icône) */
  unseen: number;
  /** Suit les exports de la société (null : plus aucun) */
  follow: (companyId: number | null) => Promise<void>;
  /** Recharge les derniers exports (après une création, ou pendant le suivi) */
  refresh: () => Promise<void>;
  markSeen: () => void;
}

let timer: ReturnType<typeof setTimeout> | null = null;
/** Société suivie, posée dès l'appel : les deux icônes (en-têtes mobile et bureau) ne chargent qu'une fois */
let followed: number | null | undefined;

/**
 * Derniers exports de la société sélectionnée (icône Téléchargements, bouton Exporter de l'Historique) : rechargés toutes
 * les EXPORT_POLL_MS tant que l'un d'eux est en attente ou en cours, plus du tout ensuite (Mercure le remplacera). Un
 * export qui passe de « en cours » à « terminé » allume la pastille, jusqu'à l'ouverture du panneau.
 */
export const useExportStore = create<ExportStore>((set, get) => ({
  companyId: null,
  exports: [],
  loaded: false,
  unseen: 0,

  follow: async (companyId) => {
    if (followed === companyId) return;
    followed = companyId;
    if (timer) clearTimeout(timer);
    timer = null;
    set({ companyId, exports: [], loaded: companyId === null, unseen: 0 });
    if (companyId !== null) await get().refresh();
  },

  refresh: async () => {
    const { companyId } = get();
    if (companyId === null) return;
    if (timer) clearTimeout(timer);
    timer = null;
    let exports: ExportModel[];
    try {
      ({ models: exports } = await listPage(ExportModel, latestExportsRequest(companyId, LATEST_EXPORTS)));
    } catch {
      // Indisponible : la liste reste celle d'avant, sans suivi
      set({ loaded: true });
      return;
    }
    // Société changée pendant le chargement : la réponse ne vaut plus
    if (get().companyId !== companyId) return;

    const wasRunning = new Set(
      get()
        .exports.filter((model) => model.isRunning)
        .map((model) => String(model.id)),
    );
    const done = exports.filter((model) => model.isCompleted && wasRunning.has(String(model.id))).length;
    set((state) => ({ exports, loaded: true, unseen: state.unseen + done }));
    if (exports.some((model) => model.isRunning)) {
      timer = setTimeout(() => void get().refresh(), EXPORT_POLL_MS);
    }
  },

  markSeen: () => set({ unseen: 0 }),
}));
